import { useCallback, useEffect, useMemo, useRef } from 'react';

import { env } from '@/env';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveGames, setSelectedGames } from '@/store/core.slice';
import { useGamesQuery } from '@/services/endpoints/games';
import {
  BACKGAMMON_GAME,
  BRIDGE_GAME,
  BRISCOLA_GAME,
  BURRACO_GAME,
  GINRUMMY_GAME,
  SCALA40_GAME,
  SETTEMEZZO_GAME,
  TRESSETTE_GAME,
  PILOTTA_GAME,
  MPIRIMPA_GAME,
  SCOPA_GAME,
  CHESS_GAME,
  CHINESE_POKER_GAME,
  COC_GAME,
  CR_GAME,
  CR_GAME_ID,
  SOLITARIO_GAME,
} from '@/lib/gameFeatures';
import { chessLimuloAcceso } from '@/lib/chess/chessLimuloAcceso';
import { qcpAcceso } from '@/lib/qcp/qcpAcceso';
import { selezionaGioco } from '@/lib/selezionaGioco';
import { storage, StorageKey } from '@/lib/storage';
import type { Game } from '@/types';
import { laravelOwnsData } from '@/lib/dataOwner';

/**
 * L'id di gioco SALVATO, se e' un id valido (numero finito e > 0), altrimenti `null`. E' il predicato della
 * reidratazione qui sotto, condiviso con chi deve sapere se un gioco «sta arrivando» nello store
 * (`LimuloTournaments`, I-342): un valore salvato non valido («0», testo, corrotto) NON viene mai reidratato, quindi
 * per chi aspetta non e' «in arrivo» — e due copie del predicato divergerebbero in silenzio.
 */
export function idGiocoSalvato(stored: string | null | undefined): number | null {
  if (!stored) return null;
  const id = Number(stored);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Game-selection state manager. Mirrors mobile-app's single-game picker
 * (`selectedGames === activeGames === [id]`) and persists to localStorage so
 * the choice survives reloads.
 *
 * Rehydrates from storage on first mount, then keeps storage in sync as the
 * user picks a different game.
 */
export function useGameSelection() {
  const dispatch = useAppDispatch();
  const selectedGames = useAppSelector((s) => s.core.selectedGames);
  const activeGames = useAppSelector((s) => s.core.activeGames);
  // ⚠️ `/games/available` e' una rotta del MONOLITE, e su una build Limulo non e' solo
  // inutile: e' l'ultima cosa che teneva vivo l'exchange-token. Trovata da or45 con uno
  // stack-probe iniettato nel runner, non per ispezione — e non si poteva trovare
  // leggendo: la query si chiama `useGamesQuery`, non ha «Laravel» nel nome, e vive due
  // livelli sotto `HubSections`, che di API Laravel non ne importa nessuna.
  // 📌 *La grep sui nomi che sembrano Laravel non lo trova; lo stack si'.*
  //
  // 🔑 La perdita e' NULLA, misurata e non stimata: il fallback qui sotto garantisce
  // `activeGame` anche quando la query fallisce — ed e' esattamente cio' che succedeva
  // gia', perche' col monolite a 503 `backendGames` era gia' vuoto. Spegnerla non cambia
  // una schermata: toglie ~9 secondi di attesa per un elenco che arrivava vuoto.
  //
  // ⚠️ E quando il monolite tornera' su, su una build Limulo quell'elenco resterebbe
  // comunque il VOCABOLARIO SBAGLIATO: i tornei Limulo portano un `gameId` che e' un
  // codice, non l'`id` numerico di Laravel (kb#81). Il selettore giusto per una build
  // Limulo si costruisce su `/v1/games` — e' un lavoro di prodotto, non uno skip.
  const { data: backendGames = [], isLoading } = useGamesQuery(undefined, {
    skip: !laravelOwnsData(),
  });
  // QChinesePoker (T0): la voce nel selettore vuole il ruolo `beta` OLTRE al predicato, come la tile della Hub e le rotte.
  const isBeta = useHasLogtoRole('beta');
  // QChess / QClans / QGammon aren't Laravel games — inject synthetic "Chess" /
  // "Clash of Clans" / "Backgammon" entries into the picker (and into activeGame
  // resolution) when their feature flags are on, so they're selectable like
  // Clash Royale / eFootball.
  const games = useMemo(() => {
    const extra: Game[] = [];
    // Clash Royale fallback: guarantees `activeGame` resolves for the default
    // game even before /games/available loads (or when it fails). The real
    // backend row (same id) wins because `find` scans backendGames first.
    if (!backendGames.some((g) => g.id === CR_GAME_ID)) extra.push(CR_GAME);
    if (env.featureChessEnabled || chessLimuloAcceso()) extra.push(CHESS_GAME);
    if (env.featureCocEnabled) extra.push(COC_GAME);
    if (env.featureBackgammonEnabled) extra.push(BACKGAMMON_GAME);
    if (qcpAcceso() && isBeta) extra.push(CHINESE_POKER_GAME);
    // QBridge (I-609): il confronto LETTERALE del flag e' quello che il bundler piega (in produzione la voce non c'e', ne' il
    // gioco sintetico), e `isBeta` e' la serratura del bridge DENTRO il selettore: l'ospite (l'altra meta' di `useBridgeAperto`)
    // il selettore non ce l'ha. Il tocco su una voce che le rotte chiudono sarebbe un vicolo cieco.
    if (import.meta.env.VITE_FEATURE_BRIDGE_ENABLED === 'true' && isBeta) extra.push(BRIDGE_GAME);
    // QBurraco (ingresso, PR1): come il Bridge, il confronto LETTERALE del flag e' quello che il bundler piega (in produzione la voce non c'e', ne' il
    // gioco sintetico) e `isBeta` e' la serratura. In piu' Limulo: le rotte del tavolo vero (`burraco/tavolo/:id`) esistono solo con Limulo acceso, e
    // «I miei tavoli» e i tornei passano dal suo BFF: senza, la voce porterebbe a una Hub che non puo' leggere niente.
    if (import.meta.env.VITE_FEATURE_BURRACO_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(BURRACO_GAME);
    // La SCOPA (kit-giochi S4): la stessa serratura del Burraco — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_SCOPA_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(SCOPA_GAME);
    // La BRISCOLA (kit-giochi P6): la stessa serratura della scopa — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_BRISCOLA_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(BRISCOLA_GAME);
    // Il SOLITARIO (TMA PR 1): la stessa serratura del Burraco — il confronto LETTERALE (piegato in produzione), Limulo (le rotte future passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_SOLITARIO_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(SOLITARIO_GAME);
    // Il GIN RUMMY (P6 del contratto del gin): la stessa serratura della scopa — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_GINRUMMY_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(GINRUMMY_GAME);
    // La SCALA 40 (P6 del contratto della scala 40): la stessa serratura del gin — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_SCALA40_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(SCALA40_GAME);
    // Il SETTE E MEZZO (P6 del contratto del sette e mezzo): la stessa serratura del gin — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_SETTEMEZZO_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(SETTEMEZZO_GAME);
    // Il TRESSETTE (P6 del contratto del tressette): la stessa serratura del sette e mezzo — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_TRESSETTE_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(TRESSETTE_GAME);
    // La PILOTTA (P8 del contratto della Pilotta): la stessa serratura del tressette — il confronto LETTERALE (piegato in produzione), Limulo (le sei rotte passano dal suo BFF) e `beta`.
    if (import.meta.env.VITE_FEATURE_PILOTTA_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(PILOTTA_GAME);
    // Il BIRIBA (PR-A, lo scheletro): la stessa serratura della pilotta — il confronto LETTERALE (piegato in produzione), Limulo e `beta`.
    if (import.meta.env.VITE_FEATURE_MPIRIMPA_ENABLED === 'true' && env.featureLimuloEnabled && isBeta) extra.push(MPIRIMPA_GAME);
    return extra.length > 0 ? [...backendGames, ...extra] : backendGames;
  }, [backendGames, isBeta]);

  const rehydrated = useRef(false);
  useEffect(() => {
    if (rehydrated.current) return;
    rehydrated.current = true;
    if (env.featureCrOnly) {
      // Single-game build: the active game is Clash Royale, period — also
      // overwrites a stale non-CR selection left over from a multi-game
      // build, which would otherwise be unfixable with the picker hidden.
      dispatch(setSelectedGames([CR_GAME_ID]));
      dispatch(setActiveGames([CR_GAME_ID]));
      storage.set(StorageKey.SelectedGameId, String(CR_GAME_ID));
      return;
    }
    const stored = storage.get(StorageKey.SelectedGameId);
    if (!stored) {
      // First run (or cleared storage): default to Clash Royale. The public
      // launch is CR-exclusive and a null activeGame renders a broken Hub
      // (no CR sections, unfiltered Rankings).
      dispatch(setSelectedGames([CR_GAME_ID]));
      dispatch(setActiveGames([CR_GAME_ID]));
      storage.set(StorageKey.SelectedGameId, String(CR_GAME_ID));
      return;
    }
    const id = idGiocoSalvato(stored);
    if (id === null) return;
    dispatch(setSelectedGames([id]));
    dispatch(setActiveGames([id]));
  }, [dispatch]);

  const activeGameId = activeGames[0] ?? null;
  const activeGame: Game | null = useMemo(() => {
    if (!activeGameId) return null;
    return games.find((g) => g.id === activeGameId) ?? null;
  }, [activeGameId, games]);

  const selectGame = useCallback(
    (id: number) => selezionaGioco(dispatch, id),
    [dispatch],
  );

  const clearGame = useCallback(() => {
    dispatch(setSelectedGames([]));
    dispatch(setActiveGames([]));
    storage.remove(StorageKey.SelectedGameId);
  }, [dispatch]);

  return {
    games,
    isLoading,
    activeGame,
    activeGameId,
    selectedGames,
    activeGames,
    selectGame,
    clearGame,
  };
}
